// Cycle-times catalogue editor inside the Settings modal. Admin-only;
// the parent (TeamModal) gates this section behind useAuth().isAdmin.
//
// Layout:
//   1. Pipe sizes — closed catalogue (DN15..DN300 seeded), with
//      inline rename, archive toggle, and "+ Add custom size" row.
//   2. Weld types — branch / flange / butt / nipple / etc., same
//      shape as pipe sizes.
//   3. Per-stage rate grids:
//      * "Average minutes" single-cell rows for take-off / picking /
//         kitting / passivation / NDT (stages where size doesn't
//         change the time);
//      * size grids for cutting / roll grooving / threading;
//      * a single shared (weld type × pipe size) grid for every weld
//         bay (rates assumed equivalent across bays — see migration
//         0020's note for the back-fill plan if that changes).
//
// Inputs save on blur so the workshop foreman can tab through a
// freshly-printed sheet without clicking "Save" between cells. An
// empty / zero entry deletes the row so the grid stays sparse.
import { useMemo, useState } from "react";
import type {
  CycleStageRateDto,
  CycleTimesDto,
  CycleWeldRateDto,
  PipeSizeDto,
  StageDefDto,
  WeldTypeDto,
} from "../../../../../../shared/types/production-schedule";
import {
  createPipeSize,
  createWeldType,
  deleteStageRate,
  deleteWeldRate,
  reorderPipeSizes,
  reorderWeldTypes,
  updatePipeSize,
  updateWeldType,
  upsertStageRate,
  upsertWeldRate,
  useCycleTimes,
} from "../hooks/useProductionApi";
import { useStagesCatalogue } from "../components/StagesProvider";

// Stage-shape convention. Keep in sync with the comment in
// migration 0020. Any stage not in PER_SIZE_STAGES and not weld=true
// falls through to the "stage average" shape; stages with
// hasHours=false (dispatched) are excluded entirely.
const PER_SIZE_STAGES: ReadonlySet<string> = new Set([
  "cutting",
  "rollgroove",
  "threading",
]);

function stageShape(stage: StageDefDto): "avg" | "per_size" | "weld" | "skip" {
  if (!stage.hasHours) return "skip";
  if (stage.weld) return "weld";
  if (PER_SIZE_STAGES.has(stage.stageKey)) return "per_size";
  return "avg";
}

export function CycleTimesSection() {
  const { data, refetch, error: fetchError } = useCycleTimes();
  const { stages } = useStagesCatalogue();
  const [error, setError] = useState<string | null>(null);

  if (!data) {
    return (
      <div style={{ marginTop: 18, padding: 12, color: "var(--bsp-fg-muted)", fontSize: 12 }}>
        Loading cycle times…
        {fetchError ? (
          <div style={{ color: "var(--bsp-red-fg)", marginTop: 4 }}>{fetchError}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 18 }}>
      <SectionHeading title="Cycle times" />
      <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Set the standard minutes for each process. The spool form
        uses these to extrapolate stage hours from the spool's
        composition (pipe size, count of branches, flanges, etc.),
        so set them once and the schedule updates itself.
      </p>
      {(error ?? fetchError) ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
          {error ?? fetchError}
        </div>
      ) : null}
      <PipeSizesEditor
        sizes={data.pipeSizes}
        onAfterMutate={refetch}
        onError={setError}
      />
      <WeldTypesEditor
        types={data.weldTypes}
        onAfterMutate={refetch}
        onError={setError}
      />
      <StageRatesPanel
        stages={stages}
        pipeSizes={data.pipeSizes}
        rates={data.stageRates}
        onAfterMutate={refetch}
        onError={setError}
      />
      <WeldRatesPanel
        weldTypes={data.weldTypes}
        pipeSizes={data.pipeSizes}
        rates={data.weldRates}
        onAfterMutate={refetch}
        onError={setError}
      />
    </div>
  );
}

// ── Sub-section heading shared with the other Settings sections ─────
function SectionHeading({ title }: { title: string }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
      paddingBottom: 8,
      borderBottom: "1px solid var(--bsp-border)",
    }}>
      <span style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 11,
        fontWeight: 700,
        color: "var(--bsp-blue)",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
      }}>{title}</span>
    </div>
  );
}

function SubHeading({ label, hint }: { label: string; hint?: string }) {
  return (
    <div style={{ marginTop: 14, marginBottom: 6 }}>
      <div style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--bsp-fg-muted)",
      }}>{label}</div>
      {hint ? (
        <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", lineHeight: 1.45, marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

// ── Pipe sizes editor ───────────────────────────────────────────────
function PipeSizesEditor({
  sizes,
  onAfterMutate,
  onError,
}: {
  sizes: PipeSizeDto[];
  onAfterMutate: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const visible = showArchived ? sizes : sizes.filter((s) => !s.archived);

  // Drag-to-reorder: drop a chip on another chip = insert before
  // it. Operates on the full catalogue so archived sizes hidden by
  // the filter still hold a stable sort_order. Persisted server-side
  // immediately; refetch via onAfterMutate keeps every consumer
  // (cycle-time grids, composition form) in sync.
  const onDrop = async (targetId: number) => {
    setDropTargetId(null);
    const source = dragId;
    setDragId(null);
    if (!source || source === targetId) return;
    const sourceIdx = sizes.findIndex((s) => s.id === source);
    const targetIdx = sizes.findIndex((s) => s.id === targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const next = sizes.slice();
    const [moved] = next.splice(sourceIdx, 1);
    if (!moved) return;
    const insertAt = next.findIndex((s) => s.id === targetId);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);
    onError(null);
    try {
      await reorderPipeSizes(next.map((s) => s.id));
      await onAfterMutate();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <SubHeading
        label="Pipe sizes"
        hint="The catalogue used by every cycle-time grid below and by the spool composition form."
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {visible.map((s) => (
          <PipeSizeChip
            key={s.id}
            row={s}
            dragging={dragId === s.id}
            dropTarget={dropTargetId === s.id}
            onPatch={async (patch) => {
              onError(null);
              try {
                await updatePipeSize(s.id, patch);
                await onAfterMutate();
              } catch (e) {
                onError(e instanceof Error ? e.message : String(e));
              }
            }}
            onDragStart={() => setDragId(s.id)}
            onDragOver={() => {
              if (dragId && dragId !== s.id && dropTargetId !== s.id) setDropTargetId(s.id);
            }}
            onDragLeave={() => {
              if (dropTargetId === s.id) setDropTargetId(null);
            }}
            onDrop={() => void onDrop(s.id)}
            onDragEnd={() => { setDragId(null); setDropTargetId(null); }}
          />
        ))}
        <AddPipeSizeForm
          onAdd={async (input) => {
            onError(null);
            try {
              await createPipeSize(input);
              await onAfterMutate();
            } catch (e) {
              onError(e instanceof Error ? e.message : String(e));
            }
          }}
        />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Show archived sizes
      </label>
    </>
  );
}

function PipeSizeChip({
  row,
  dragging,
  dropTarget,
  onPatch,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  row: PipeSizeDto;
  dragging: boolean;
  dropTarget: boolean;
  onPatch: (patch: { label?: string; nbMm?: number; archived?: boolean }) => Promise<void>;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px 4px 4px",
        borderRadius: 999,
        // Drop-target hint — left-border ticks up so the user sees
        // where the dropped chip will land.
        border: dropTarget
          ? "1px solid var(--bsp-blue)"
          : "1px solid var(--bsp-border)",
        boxShadow: dropTarget
          ? "inset 3px 0 0 var(--bsp-blue)"
          : undefined,
        background: row.archived ? "var(--bsp-surface-muted)" : "var(--bsp-surface)",
        fontSize: 11,
        fontFamily: "var(--bsp-font-mono)",
        opacity: dragging ? 0.4 : row.archived ? 0.5 : 1,
      }}
      onDragOver={(e) => {
        if (dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver();
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onDragLeave();
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      <span
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", `pipe:${row.id}`);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title="Drag to reorder"
        style={{
          cursor: "grab",
          color: "var(--bsp-fg-muted)",
          padding: "0 2px",
          userSelect: "none",
        }}
      >
        ⋮⋮
      </span>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => { if (label !== row.label && label.trim()) void onPatch({ label: label.trim() }); }}
        style={{
          width: 60,
          border: "none",
          background: "transparent",
          fontFamily: "var(--bsp-font-mono)",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--bsp-fg)",
          padding: 0,
        }}
      />
      <button
        type="button"
        onClick={() => void onPatch({ archived: !row.archived })}
        title={row.archived ? "Restore" : "Archive"}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--bsp-fg-muted)",
          cursor: "pointer",
          padding: 0,
          fontSize: 11,
        }}
      >
        {row.archived ? "↩" : "×"}
      </button>
    </span>
  );
}

function AddPipeSizeForm({
  onAdd,
}: { onAdd: (input: { label: string; nbMm: number }) => Promise<void> }) {
  const [label, setLabel] = useState("");
  const submit = async () => {
    const trimmed = label.trim().toUpperCase();
    if (!trimmed) return;
    // Parse nb_mm from the label — the catalogue convention is
    // DN<number>, so an "DN65" label gives nb_mm = 65. Falls back to
    // 0 if the label doesn't carry the convention; the admin can
    // edit the row afterwards via direct API if needed.
    const m = trimmed.match(/(\d+)/);
    const nbMm = m ? Number(m[1]) : 0;
    await onAdd({ label: trimmed, nbMm });
    setLabel("");
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        placeholder="DN…"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
        style={{
          width: 64,
          padding: "4px 8px",
          fontSize: 11,
          fontFamily: "var(--bsp-font-mono)",
          border: "1px dashed var(--bsp-border-strong)",
          borderRadius: 999,
          background: "var(--bsp-surface-muted)",
        }}
      />
      <button
        type="button"
        className="btn-sm"
        disabled={!label.trim()}
        onClick={() => void submit()}
      >+</button>
    </span>
  );
}

// ── Weld types editor ───────────────────────────────────────────────
function WeldTypesEditor({
  types,
  onAfterMutate,
  onError,
}: {
  types: WeldTypeDto[];
  onAfterMutate: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const visible = showArchived ? types : types.filter((t) => !t.archived);

  const onDrop = async (targetId: number) => {
    setDropTargetId(null);
    const source = dragId;
    setDragId(null);
    if (!source || source === targetId) return;
    const sourceIdx = types.findIndex((t) => t.id === source);
    const targetIdx = types.findIndex((t) => t.id === targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const next = types.slice();
    const [moved] = next.splice(sourceIdx, 1);
    if (!moved) return;
    const insertAt = next.findIndex((t) => t.id === targetId);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);
    onError(null);
    try {
      await reorderWeldTypes(next.map((t) => t.id));
      await onAfterMutate();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <SubHeading
        label="Weld types"
        hint="Each row gets a minutes-per-pipe-size grid in the welding section below. Add custom types for branch reinforcements, air bottles, etc."
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {visible.map((t) => (
          <WeldTypeChip
            key={t.id}
            row={t}
            dragging={dragId === t.id}
            dropTarget={dropTargetId === t.id}
            onPatch={async (patch) => {
              onError(null);
              try {
                await updateWeldType(t.id, patch);
                await onAfterMutate();
              } catch (e) {
                onError(e instanceof Error ? e.message : String(e));
              }
            }}
            onDragStart={() => setDragId(t.id)}
            onDragOver={() => {
              if (dragId && dragId !== t.id && dropTargetId !== t.id) setDropTargetId(t.id);
            }}
            onDragLeave={() => {
              if (dropTargetId === t.id) setDropTargetId(null);
            }}
            onDrop={() => void onDrop(t.id)}
            onDragEnd={() => { setDragId(null); setDropTargetId(null); }}
          />
        ))}
        <AddWeldTypeForm
          onAdd={async (label) => {
            onError(null);
            try {
              await createWeldType({ label });
              await onAfterMutate();
            } catch (e) {
              onError(e instanceof Error ? e.message : String(e));
            }
          }}
        />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Show archived types
      </label>
    </>
  );
}

function WeldTypeChip({
  row,
  dragging,
  dropTarget,
  onPatch,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  row: WeldTypeDto;
  dragging: boolean;
  dropTarget: boolean;
  onPatch: (patch: { label?: string; archived?: boolean }) => Promise<void>;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 10px 4px 4px",
        borderRadius: 999,
        border: dropTarget ? "1px solid var(--bsp-blue)" : "1px solid var(--bsp-border)",
        boxShadow: dropTarget ? "inset 3px 0 0 var(--bsp-blue)" : undefined,
        background: row.archived ? "var(--bsp-surface-muted)" : "var(--bsp-surface)",
        fontSize: 11,
        opacity: dragging ? 0.4 : row.archived ? 0.5 : 1,
      }}
      onDragOver={(e) => {
        if (dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver();
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onDragLeave();
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      <span
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", `weld:${row.id}`);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title="Drag to reorder"
        style={{
          cursor: "grab",
          color: "var(--bsp-fg-muted)",
          padding: "0 2px",
          userSelect: "none",
        }}
      >
        ⋮⋮
      </span>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => { if (label !== row.label && label.trim()) void onPatch({ label: label.trim() }); }}
        style={{
          width: 90,
          border: "none",
          background: "transparent",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--bsp-fg)",
          padding: 0,
        }}
      />
      <button
        type="button"
        onClick={() => void onPatch({ archived: !row.archived })}
        title={row.archived ? "Restore" : "Archive"}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--bsp-fg-muted)",
          cursor: "pointer",
          padding: 0,
          fontSize: 11,
        }}
      >
        {row.archived ? "↩" : "×"}
      </button>
    </span>
  );
}

function AddWeldTypeForm({ onAdd }: { onAdd: (label: string) => Promise<void> }) {
  const [label, setLabel] = useState("");
  const submit = async () => {
    if (!label.trim()) return;
    await onAdd(label.trim());
    setLabel("");
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        placeholder="e.g. Stub-in"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
        style={{
          width: 110,
          padding: "4px 10px",
          fontSize: 11,
          border: "1px dashed var(--bsp-border-strong)",
          borderRadius: 999,
          background: "var(--bsp-surface-muted)",
        }}
      />
      <button
        type="button"
        className="btn-sm"
        disabled={!label.trim()}
        onClick={() => void submit()}
      >+</button>
    </span>
  );
}

// ── Stage rates (avg + per-size) ────────────────────────────────────
function StageRatesPanel({
  stages,
  pipeSizes,
  rates,
  onAfterMutate,
  onError,
}: {
  stages: StageDefDto[];
  pipeSizes: PipeSizeDto[];
  rates: CycleStageRateDto[];
  onAfterMutate: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const activeSizes = useMemo(
    () => pipeSizes.filter((p) => !p.archived).sort((a, b) => a.nbMm - b.nbMm),
    [pipeSizes],
  );
  const rateByKey = useMemo(() => {
    const map = new Map<string, CycleStageRateDto>();
    for (const r of rates) map.set(`${r.stageKey}:${r.pipeSizeId ?? "null"}`, r);
    return map;
  }, [rates]);

  const nonWeld = useMemo(
    () => stages.filter((s) => !s.archived && stageShape(s) !== "skip" && stageShape(s) !== "weld"),
    [stages],
  );
  const avgStages = nonWeld.filter((s) => stageShape(s) === "avg");
  const perSizeStages = nonWeld.filter((s) => stageShape(s) === "per_size");

  const saveStageRate = async (stageKey: string, pipeSizeId: number | null, minutesStr: string) => {
    onError(null);
    const trimmed = minutesStr.trim();
    try {
      if (trimmed === "" || Number(trimmed) === 0) {
        await deleteStageRate(stageKey, pipeSizeId);
      } else {
        const minutes = Number(trimmed);
        if (!Number.isFinite(minutes) || minutes < 0) {
          onError("Minutes must be a non-negative number");
          return;
        }
        await upsertStageRate({ stageKey, pipeSizeId, minutes });
      }
      await onAfterMutate();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <SubHeading
        label="Stage averages"
        hint="One number per stage — used for processes where size doesn't change the time."
      />
      <table className="stages-table" style={{ marginBottom: 14 }}>
        <thead>
          <tr>
            <th>Stage</th>
            <th style={{ width: 140 }}>Avg. minutes</th>
          </tr>
        </thead>
        <tbody>
          {avgStages.length === 0 ? (
            <tr><td colSpan={2} style={{ color: "var(--bsp-fg-muted)", fontSize: 11 }}>No average-time stages.</td></tr>
          ) : avgStages.map((s) => {
            const rate = rateByKey.get(`${s.stageKey}:null`);
            return (
              <MinutesCell
                key={s.stageKey}
                renderCellTag="tr"
                rowLabel={s.name}
                color={s.color}
                initial={rate ? String(rate.minutes) : ""}
                onSave={(v) => saveStageRate(s.stageKey, null, v)}
              />
            );
          })}
        </tbody>
      </table>

      <SubHeading
        label="Per-size minutes"
        hint="Cutting, roll grooving, threading — minutes per length at each pipe size."
      />
      <div style={{ overflowX: "auto", marginBottom: 14 }}>
        <table className="stages-table">
          <thead>
            <tr>
              <th>Stage</th>
              {activeSizes.map((p) => (
                <th key={p.id} style={{ width: 80, textAlign: "center", fontFamily: "var(--bsp-font-mono)" }}>
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perSizeStages.length === 0 ? (
              <tr><td colSpan={activeSizes.length + 1} style={{ color: "var(--bsp-fg-muted)", fontSize: 11 }}>No per-size stages.</td></tr>
            ) : perSizeStages.map((s) => (
              <tr key={s.stageKey}>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                    {s.name}
                  </span>
                </td>
                {activeSizes.map((p) => {
                  const rate = rateByKey.get(`${s.stageKey}:${p.id}`);
                  return (
                    <MinutesGridCell
                      key={p.id}
                      initial={rate ? String(rate.minutes) : ""}
                      onSave={(v) => saveStageRate(s.stageKey, p.id, v)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Weld rates (shared across all weld bays) ────────────────────────
function WeldRatesPanel({
  weldTypes,
  pipeSizes,
  rates,
  onAfterMutate,
  onError,
}: {
  weldTypes: WeldTypeDto[];
  pipeSizes: PipeSizeDto[];
  rates: CycleWeldRateDto[];
  onAfterMutate: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const activeSizes = useMemo(
    () => pipeSizes.filter((p) => !p.archived).sort((a, b) => a.nbMm - b.nbMm),
    [pipeSizes],
  );
  const activeTypes = useMemo(
    () => weldTypes.filter((t) => !t.archived).sort((a, b) => a.sortOrder - b.sortOrder),
    [weldTypes],
  );
  const rateByKey = useMemo(() => {
    const map = new Map<string, CycleWeldRateDto>();
    for (const r of rates) map.set(`${r.weldTypeId}:${r.pipeSizeId}`, r);
    return map;
  }, [rates]);

  const save = async (weldTypeId: number, pipeSizeId: number, minutesStr: string) => {
    onError(null);
    const trimmed = minutesStr.trim();
    try {
      if (trimmed === "" || Number(trimmed) === 0) {
        await deleteWeldRate(weldTypeId, pipeSizeId);
      } else {
        const minutes = Number(trimmed);
        if (!Number.isFinite(minutes) || minutes < 0) {
          onError("Minutes must be a non-negative number");
          return;
        }
        await upsertWeldRate({ weldTypeId, pipeSizeId, minutes });
      }
      await onAfterMutate();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <SubHeading
        label="Welding"
        hint="One grid shared across every weld bay. Minutes per weld at each (weld type × pipe size)."
      />
      <div style={{ overflowX: "auto" }}>
        <table className="stages-table">
          <thead>
            <tr>
              <th>Weld type</th>
              {activeSizes.map((p) => (
                <th key={p.id} style={{ width: 80, textAlign: "center", fontFamily: "var(--bsp-font-mono)" }}>
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeTypes.length === 0 ? (
              <tr><td colSpan={activeSizes.length + 1} style={{ color: "var(--bsp-fg-muted)", fontSize: 11 }}>No weld types yet.</td></tr>
            ) : activeTypes.map((t) => (
              <tr key={t.id}>
                <td>{t.label}</td>
                {activeSizes.map((p) => {
                  const rate = rateByKey.get(`${t.id}:${p.id}`);
                  return (
                    <MinutesGridCell
                      key={p.id}
                      initial={rate ? String(rate.minutes) : ""}
                      onSave={(v) => save(t.id, p.id, v)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Cells ───────────────────────────────────────────────────────────
function MinutesCell({
  rowLabel,
  color,
  initial,
  onSave,
}: {
  rowLabel: string;
  color: string;
  initial: string;
  onSave: (v: string) => Promise<void>;
  renderCellTag?: "tr";
}) {
  const [val, setVal] = useState(initial);
  return (
    <tr>
      <td>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
          {rowLabel}
        </span>
      </td>
      <td>
        <input
          type="number"
          min={0}
          step={0.5}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => { if (val !== initial) void onSave(val); }}
          style={{
            width: "100%",
            border: "1px solid var(--bsp-border)",
            borderRadius: 4,
            padding: "4px 8px",
            fontSize: 12,
            fontFamily: "var(--bsp-font-mono)",
            textAlign: "right",
            background: val ? "var(--bsp-surface)" : "var(--bsp-surface-muted)",
          }}
        />
      </td>
    </tr>
  );
}

function MinutesGridCell({
  initial,
  onSave,
}: {
  initial: string;
  onSave: (v: string) => Promise<void>;
}) {
  const [val, setVal] = useState(initial);
  return (
    <td style={{ padding: 2 }}>
      <input
        type="number"
        min={0}
        step={0.5}
        value={val}
        placeholder="—"
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { if (val !== initial) void onSave(val); }}
        style={{
          width: "100%",
          border: "1px solid var(--bsp-border)",
          borderRadius: 4,
          padding: "4px 6px",
          fontSize: 12,
          fontFamily: "var(--bsp-font-mono)",
          textAlign: "right",
          background: val ? "var(--bsp-surface)" : "var(--bsp-surface-muted)",
          color: val ? "var(--bsp-fg)" : "var(--bsp-fg-muted)",
        }}
      />
    </td>
  );
}
